import { useEffect, useState, type ReactNode } from "react";
import { api, type Conference, type ConferencePreview } from "../../api.js";
import { apiErrorMessage } from "../../../api/client.js";
import { Alert, EmptyState, Skeleton } from "../../../design/components/Feedback.js";

export function StageHeader({ title, description }: { title: string; description: string }) {
  return <header className="conference-stage-header"><div><h1>{title}</h1><p>{description}</p></div></header>;
}

export function StagePanel({ label, title, description, children }: { label: string; title: string; description?: string; children: ReactNode }) {
  return <section className="conference-stage-panel" aria-label={label}><header><h2>{title}</h2>{description ? <p>{description}</p> : null}</header>{children}</section>;
}

export function useConferenceProjection(conference: Conference) {
  const [data, setData] = useState<ConferencePreview | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    api.conferencePreview(conference.id, conference.organization_id)
      .then((result) => { if (active) setData(result); })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "This conference data could not be loaded.")); });
    return () => { active = false; };
  }, [conference.id, conference.organization_id]);
  return { data, error };
}

export function ProjectionState({ data, error, emptyTitle, emptyDescription, hasRows, children }: { data: ConferencePreview | null; error: string; emptyTitle: string; emptyDescription: string; hasRows: boolean; children: ReactNode }) {
  if (error) return <Alert tone="danger">{error}</Alert>;
  if (!data) return <div className="conference-stage-loading" aria-label="Loading conference data"><Skeleton /><Skeleton /></div>;
  if (!hasRows) return <EmptyState title={emptyTitle} description={emptyDescription} />;
  return <>{children}</>;
}

export function StageTable({ headers, rows }: { headers: string[]; rows: ReactNode[][] }) {
  return <div className="conference-stage-table" tabIndex={0}><table><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
}
